import { Heading, Text } from "@/components/ui"

export interface StatWallItem {
  big: string
  label: string
}

const GROUND = ["bg-brand", "bg-brand-400", "bg-brand-300", "bg-brand-200"] as const

const RING = [
  { count: 16, radius: 42.7, sizes: [1.92, 2.5, 2.71, 2.5] },
  { count: 8, radius: 31.2, sizes: [1.92, 2.71] },
]

const DotRing = () => (
  <svg viewBox="0 0 100 100" aria-hidden className="absolute inset-0 size-full">
    {RING.flatMap(({ count, radius, sizes }, ringIndex) =>
      Array.from({ length: count }, (_, index) => {
        const angle = (index / count) * 2 * Math.PI - Math.PI / 2
        return (
          <circle
            key={`${ringIndex}-${index}`}
            cx={50 + radius * Math.cos(angle)}
            cy={50 + radius * Math.sin(angle)}
            r={sizes[index % sizes.length]}
            fill="white"
            opacity={index % 4 === 1 ? 0.45 : 0.9}
          />
        )
      })
    )}
  </svg>
)

const CARD_BOX = "0 0 413 251"
const BAR_BASELINE = 221

const BARS = [
  { x: 223, height: 72, opacity: 0.35 },
  { x: 278, height: 141, opacity: 0.55 },
  { x: 333, height: 191, opacity: 1 },
]

const Bars = () => (
  <svg viewBox={CARD_BOX} preserveAspectRatio="xMaxYMax slice" aria-hidden className="absolute inset-0 size-full">
    {BARS.map(({ x, height, opacity }) => (
      <rect key={x} x={x} y={BAR_BASELINE - height} width={50} height={height} rx={25} fill="white" opacity={opacity} />
    ))}
  </svg>
)

const Donut = () => (
  <svg viewBox={CARD_BOX} preserveAspectRatio="xMaxYMax slice" aria-hidden className="absolute inset-0 size-full">
    <circle cx="412.5" cy="125.5" r="110" fill="none" stroke="white" strokeWidth="18" strokeDasharray="115 576" opacity="0.35" transform="rotate(215 412.5 125.5)" />
    <circle cx="412.5" cy="125.5" r="85" fill="none" stroke="white" strokeWidth="37" strokeDasharray="445 89" transform="rotate(30 412.5 125.5)" />
  </svg>
)

const MATRIX_COLUMNS = 7
const MATRIX_ROWS = 5

const DotMatrix = () => (
  <div aria-hidden className="grid gap-100" style={{ gridTemplateColumns: `repeat(${MATRIX_COLUMNS}, minmax(0, 1fr))` }}>
    {Array.from({ length: MATRIX_ROWS * MATRIX_COLUMNS }, (_, index) => {
      const row = Math.floor(index / MATRIX_COLUMNS)
      const column = index % MATRIX_COLUMNS
      const arm = Math.abs(column - 3)
      const solid = arm === row || arm === row - 2
      return <span key={index} className={`aspect-square rounded-full bg-white ${solid ? "" : "opacity-25"}`} />
    })}
  </div>
)

const PLACEMENT = [
  "@3xl:col-start-1 @3xl:row-start-1 @3xl:row-span-2",
  "@3xl:col-start-2 @3xl:row-start-1",
  "@3xl:col-start-2 @3xl:row-start-2",
  "@3xl:col-start-3 @3xl:row-start-1 @3xl:row-span-2",
]

const MEASURE = ["max-w-measure-sm @2xl:max-w-1/2 @3xl:max-w-2/5", "max-w-3/5", "max-w-3/5", ""]

const HEIGHT = ["aspect-square @3xl:aspect-auto", "min-h-38", "min-h-38", "min-h-78"]

const StatCard = ({ stat, index }: { stat: StatWallItem; index: number }) => {
  const ground = GROUND[index % GROUND.length]
  const centred = index === 0
  return (
    <div
      className={`${ground} ${PLACEMENT[index] ?? ""} ${HEIGHT[index] ?? ""} rounded-12x relative isolate flex flex-col overflow-hidden p-500 @3xl:min-h-0 @3xl:p-700 ${centred ? "items-center justify-center text-center" : "justify-start"} ${index === 3 ? "gap-600 @3xl:justify-between" : ""}`}
    >
      {centred && <DotRing />}
      {index === 1 && <Bars />}
      {index === 2 && <Donut />}
      <div className={`relative z-10 ${MEASURE[index] ?? "max-w-3/5"}`}>
        <Heading as="p" variant="heroSplit" family="hero" tone="inverted">
          {stat.big}
        </Heading>
        <Text as="p" variant="body" family="text" tone="inverted" className="@3xl:text-body-lg @3xl:mt-250">
          {stat.label}
        </Text>
      </div>
      {index === 3 && (
        <div className="relative z-10">
          <DotMatrix />
        </div>
      )}
    </div>
  )
}

export const StatWall = ({ stats }: { stats: StatWallItem[] }) => (
  <div className="@container">
    <div className="@3xl:aspect-stat-wall @3xl:grid-cols-stat-wall grid gap-250 @2xl:grid-cols-2 @3xl:grid-rows-2 @3xl:gap-500">
      {stats.map((stat, index) => (
        <StatCard key={stat.label} stat={stat} index={index} />
      ))}
    </div>
  </div>
)
